import type { FormInstance } from '@arco-design/web-react';
import { ZodError } from 'zod';

import type { DeployProjectionProfile, ModuleMeta, PipelineContext, SkuSubmitResult, ZodSchema } from '../../core/types';
import { mapDescriptorsToDisplayConfig } from '../display-descriptors';
import { SKU_RESOURCE_MODULES_FIELD } from '../scene-layout/types';
import { omitUncheckedSkuModuleFormFields } from '../scene-layout/utils/omitUncheckedSkuModuleFormFields';
import { stringifyVersionedPreset } from '../versioning';
import { mergeOverlay } from './mergeOverlay';
import type { ResourceType } from './types';

/** 与 resolveEnabledModulesForSpecFilter 同源：优先表单勾选态（避免 kernel→plugins 依赖） */
function resolveEnabledModulesFromForm(ctx: PipelineContext, values: Record<string, unknown>): Record<string, boolean> | undefined {
  const fromForm = values[SKU_RESOURCE_MODULES_FIELD] as Record<string, boolean> | undefined;
  if (fromForm && typeof fromForm === 'object' && Object.keys(fromForm).length > 0) {
    return fromForm;
  }
  const fromCtx = ctx.sku?.enabledModules;
  if (fromCtx && typeof fromCtx === 'object' && Object.keys(fromCtx).length > 0) {
    return fromCtx;
  }
  return undefined;
}

export interface PipelineKernelInput<TSpec, TPayload, TSide = unknown> {
  schema: ZodSchema<TSpec>;
  fromForm: (values: Record<string, unknown>, ctx: PipelineContext, side?: TSide) => TSpec;
  toForm: (spec: TSpec, ctx?: PipelineContext) => Record<string, unknown>;
  getInitialFormValues: (ctx?: PipelineContext) => Record<string, unknown>;
  projectionProfile: DeployProjectionProfile<TSpec, TPayload>;
  /** 提交前校验（必填项）；serialize 仅做结构映射 */
  assertSubmitReady?: (spec: TSpec, ctx: PipelineContext) => void;
  /** 未勾选模块字段裁剪（直建 / 下单 / SKU 统一） */
  modules?: readonly ModuleMeta[];
}

/** validateSubmit / buildSpecFromForm 共用：校验后的规格 + 刷新过 enabledModules 的 ctx */
export interface PipelineBuildResult<TSpec> {
  spec: TSpec;
  ctx: PipelineContext;
}

export interface PipelineKernel<TSpec, TPayload, TSide = unknown> {
  schema: ZodSchema<TSpec>;
  fromForm: PipelineKernelInput<TSpec, TPayload, TSide>['fromForm'];
  toForm: PipelineKernelInput<TSpec, TPayload, TSide>['toForm'];
  getInitialFormValues: PipelineKernelInput<TSpec, TPayload, TSide>['getInitialFormValues'];
  projectionProfile: DeployProjectionProfile<TSpec, TPayload>;
  parseSpec: (input: unknown) => TSpec;
  buildSubmitPayload: (form: FormInstance, ctx: PipelineContext, side?: TSide) => TPayload;
  /** 提交前校验；返回 spec 与刷新后的 ctx（含表单勾选态） */
  validateSubmit: (form: FormInstance, ctx: PipelineContext, side?: TSide) => PipelineBuildResult<TSpec>;
  buildSkuResourceConfigs: (form: FormInstance, ctx: PipelineContext, side?: TSide) => ResourceType[];
  prepareSkuSubmit: (form: FormInstance, ctx: PipelineContext, side?: TSide) => SkuSubmitResult;
}

/**
 * 读取表单完整 store。
 * `getFieldsValue()` 仅含已注册 FormItem，编辑回填 / 弹窗写入但尚未挂载的字段会被漏掉（如 systemDiskPricing）。
 */
function readFormValues(form: FormInstance): Record<string, unknown> {
  return form.getFields() as Record<string, unknown>;
}

function assertSkuCapable<TSpec, TPayload>(
  profile: DeployProjectionProfile<TSpec, TPayload>,
): asserts profile is Extract<DeployProjectionProfile<TSpec, TPayload>, { profile: 'sku-capable' }> {
  if (profile.profile !== 'sku-capable') {
    throw new Error(`Expected sku-capable projection profile, got "${profile.profile}"`);
  }
}

export function createPipelineKernel<TSpec, TPayload extends Record<string, unknown>, TSide = unknown>(
  impl: PipelineKernelInput<TSpec, TPayload, TSide>,
): PipelineKernel<TSpec, TPayload, TSide> {
  const { schema, fromForm, toForm, getInitialFormValues, projectionProfile, assertSubmitReady, modules = [] } = impl;

  const parseSpec = (input: unknown): TSpec => schema.parse(input);

  /** 未勾选模块字段先从表单裁掉，再 fromForm（与场景无关） */
  const readValuesForPipeline = (form: FormInstance): Record<string, unknown> =>
    omitUncheckedSkuModuleFormFields(readFormValues(form), modules);

  /**
   * pipelineCtx 常被 useMemo 冻结；每次读表单刷新 enabledModules，
   * 避免勾选节点组后算价仍用旧的 false。
   */
  const withFreshEnabledModules = (ctx: PipelineContext, values: Record<string, unknown>): PipelineContext => {
    const enabledModules = resolveEnabledModulesFromForm(ctx, values);
    if (enabledModules === undefined && !ctx.sku) return ctx;
    return {
      ...ctx,
      sku: {
        ...ctx.sku,
        ...(enabledModules !== undefined ? { enabledModules } : {}),
      },
    };
  };

  const buildSpecFromForm = (form: FormInstance, ctx: PipelineContext, side?: TSide): PipelineBuildResult<TSpec> => {
    const values = readValuesForPipeline(form);
    const freshCtx = withFreshEnabledModules(ctx, values);
    const raw = fromForm(values, freshCtx, side);
    return { spec: parseSpec(raw), ctx: freshCtx };
  };

  const validateSubmit = (form: FormInstance, ctx: PipelineContext, side?: TSide): PipelineBuildResult<TSpec> => {
    const built = buildSpecFromForm(form, ctx, side);
    assertSubmitReady?.(built.spec, built.ctx);
    return built;
  };

  const buildSubmitPayload = (form: FormInstance, ctx: PipelineContext, side?: TSide): TPayload => {
    const { spec, ctx: freshCtx } = validateSubmit(form, ctx, side);
    const body = projectionProfile.projections.apiBody(spec, freshCtx);
    const result = mergeOverlay(body, freshCtx.overlay) as Record<string, unknown>;
    return result as TPayload;
  };

  const buildSkuResourceConfigs = (form: FormInstance, ctx: PipelineContext, side?: TSide): ResourceType[] => {
    assertSkuCapable(projectionProfile);
    try {
      const { spec, ctx: freshCtx } = buildSpecFromForm(form, ctx, side);
      return projectionProfile.projections.skuResources(spec, freshCtx);
    } catch (error) {
      if (error instanceof ZodError) return [];
      throw error;
    }
  };

  const prepareSkuSubmit = (form: FormInstance, ctx: PipelineContext, side?: TSide): SkuSubmitResult => {
    assertSkuCapable(projectionProfile);
    const values = readValuesForPipeline(form);
    const freshCtx = withFreshEnabledModules(ctx, values);
    const spec = parseSpec(fromForm(values, freshCtx, side));
    const { projections } = projectionProfile;
    const descriptors = projections.buildDisplayDescriptors(spec, { ...freshCtx, formValues: values });
    return {
      presetConfig: stringifyVersionedPreset(values),
      displayConfig: mapDescriptorsToDisplayConfig(descriptors),
      resourceConfigs: projections.skuResources(spec, freshCtx),
    };
  };

  return {
    schema,
    fromForm,
    toForm,
    getInitialFormValues,
    projectionProfile,
    parseSpec,
    buildSubmitPayload,
    validateSubmit,
    buildSkuResourceConfigs,
    prepareSkuSubmit,
  };
}

export function isSkuCapableProfile<TSpec, TPayload>(
  profile: DeployProjectionProfile<TSpec, TPayload>,
): profile is Extract<DeployProjectionProfile<TSpec, TPayload>, { profile: 'sku-capable' }> {
  return profile.profile === 'sku-capable';
}
